import type { RefObject } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';

import { getTextHeight } from 'src/app/utils/get-text-height';

export type TextGetter = ({
  displayedOptions,
  hiddenOptions,
}: {
  displayedOptions: string[];
  hiddenOptions: string[];
}) => string;

export type UseListWithMaxHeightProps = {
  font: string;
  getText?: TextGetter;
  maxListHeight: number;
  options: string[];
  ref: RefObject<HTMLElement>;
};

/**
 * A hook used to help create a UI element that displays a comma-separated list of options (strings)
 *  up until the height of the list as rendered exceeds a pre-determined maximum.
 * The hook assumes that when the maximum height has been exceeded, a `+ {count} more` tooltip indicator text will be appended to the list
 *  where {count} is the number of hidden items.
 * To override this behavior, pass a `getText` prop which the hook will call in place of the default rendering.
 *
 * @param arg single argument object with five parameters:
 *   font: string - the font that will be used to render the list
 *   getText?: TextGetter - optional text rendering function if the default rendering is not desired
 *   maxListHeight: number - the height the rendered list should not exceed
 *   options: string[] - the entire list of options to be rendered
 *   ref: RefObject<HTMLElement>: a ref for the element that will contain the list
 * @returns: an object with two parameters:
 *   displayedOptions: a list of options that can be safely rendered (including the + {count} more text) with no height overflow
 *   hiddenOptions: the options that need to be displayed in the tooltip as overflow
 */
export function useListWithMaxHeight({ font, getText, maxListHeight, options, ref }: UseListWithMaxHeightProps) {
  const [hiddenOptions, setHiddenOptions] = useState<string[]>([]);
  const [displayedOptions, setDisplayedOptions] = useState<string[]>(options);

  useEffect(() => {
    setDisplayedOptions(options);
    setHiddenOptions([]);
  }, [options]);

  useLayoutEffect(() => {
    if (options.length === 0) {
      return;
    }

    const boundingRectangle = ref?.current?.getBoundingClientRect();
    const renderedHeight = boundingRectangle?.height ?? 0;
    const renderedWidth = boundingRectangle?.width ?? 0;

    if (renderedHeight > maxListHeight) {
      const newDisplayedOptions = [...displayedOptions];
      const newHiddenOptions: string[] = [];
      let textHeight = renderedHeight;
      while (textHeight > maxListHeight) {
        const removedOption = newDisplayedOptions.pop() ?? '';
        newHiddenOptions.push(removedOption);
        const textToTest = getText
          ? getText({ displayedOptions: newDisplayedOptions, hiddenOptions: newHiddenOptions })
          : `${newDisplayedOptions.join(', ')} + ${newHiddenOptions.length} more`;
        textHeight = getTextHeight({ font, text: textToTest, width: renderedWidth });
      }

      setHiddenOptions(newHiddenOptions);
      setDisplayedOptions(newDisplayedOptions);
    }
  }, [displayedOptions, font, getText, hiddenOptions, maxListHeight, options, ref]);

  return {
    displayedOptions,
    hiddenOptions,
  };
}
